import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Alert, 
  TextField, 
  IconButton, 
  Tooltip,
  Divider,
  CircularProgress
} from '@mui/material';
import { 
  ContentCopy as ContentCopyIcon, 
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { createShare, getAis } from '../services/api';

interface Collection {
  _id: string;
  version: string;
  instruction?: string;
}

interface AI {
  id: string;
  title: string;
  domain: string;
  description: string;
  collections: Collection[];
}

const ShareAi = () => {
  const { aiId } = useParams<{ aiId: string }>();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get('collectionId');
  const navigate = useNavigate();

  const [ai, setAi] = useState<AI | null>(null);
  const [collection, setCollection] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareData, setShareData] = useState<{
    shareCode: string;
    password: string;
    expiresAt: string;
  } | null>(null);

  useEffect(() => {
    if (!aiId || !collectionId) {
      setError('AI ID and Collection ID are required');
      setLoading(false);
      return;
    }

    // Fetch AI details
    getAis()
      .then((res) => {
        const currentAi = res.data.find((ai: AI) => ai.id === aiId);
        if (currentAi) {
          setAi(currentAi);
          const currentCollection = currentAi.collections.find((col: Collection) => col._id === collectionId);
          if (currentCollection) {
            setCollection(currentCollection);
          } else {
            setError('Collection not found');
          }
        } else {
          setError('AI not found');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading AI:', err);
        setError('Failed to load AI details');
        setLoading(false);
      });
  }, [aiId, collectionId]);

  const handleCreateShare = async () => {
    if (!aiId || !collectionId) return;

    setSharing(true);
    setError(null);

    try {
      const response = await createShare(aiId, collectionId);
      setShareData({
        shareCode: response.data.shareCode,
        password: response.data.password,
        expiresAt: response.data.expiresAt,
      });
    } catch (err: any) {
      console.error('Error creating share:', err);
      setError(err.response?.data?.error || 'Failed to create share');
    } finally {
      setSharing(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
      console.log('Copied to clipboard:', text);
    }).catch((err) => {
      console.error('Failed to copy text:', err);
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/my-ais')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <ShareIcon sx={{ mr: 1 }} />
          Share AI
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {ai && collection && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sharing Details
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            <strong>AI:</strong> {ai.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            <strong>Version:</strong> {collection.version}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            <strong>Domain:</strong> {ai.domain}
          </Typography>
          {ai.description && (
            <Typography variant="body1" color="text.secondary" gutterBottom>
              <strong>Description:</strong> {ai.description}
            </Typography>
          )}
        </Paper>
      )}

      {!shareData ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Create Share Codes
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Generate share codes to allow other users to import and use your AI.
            The share will expire in 30 days.
          </Typography>
          <Button
            variant="contained"
            onClick={handleCreateShare}
            disabled={sharing}
            size="large"
            startIcon={sharing ? <CircularProgress size={20} /> : <ShareIcon />}
          >
            {sharing ? 'Creating Share...' : 'Create Share'}
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Share codes created successfully! Share these codes with other users to allow them to import your AI.
          </Alert>
          
          <Typography variant="h6" gutterBottom>
            Share Information
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Share Code (AI ID + Collection ID):
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={shareData.shareCode}
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: true,
                }}
                sx={{ fontFamily: 'monospace' }}
              />
              <Tooltip title="Copy share code">
                <IconButton onClick={() => copyToClipboard(shareData.shareCode)}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Password:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={shareData.password}
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: true,
                }}
                sx={{ fontFamily: 'monospace' }}
              />
              <Tooltip title="Copy password">
                <IconButton onClick={() => copyToClipboard(shareData.password)}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Expires:</strong> {new Date(shareData.expiresAt).toLocaleDateString()}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>Instructions for recipients:</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            1. Go to the "My AIs" page
          </Typography>
          <Typography variant="body2" color="text.secondary">
            2. Click the "Import AI" button
          </Typography>
          <Typography variant="body2" color="text.secondary">
            3. Enter the share code and password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            4. Click "Import" to create their own copy
          </Typography>
        </Paper>
      )}

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/my-ais')}
          fullWidth
        >
          Back to My AIs
        </Button>
        {shareData && (
          <Button
            variant="outlined"
            onClick={() => {
              const text = `Share Code: ${shareData.shareCode}\nPassword: ${shareData.password}`;
              copyToClipboard(text);
            }}
            fullWidth
          >
            Copy Both Codes
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ShareAi;
